// Vite entry point — replaces Typescript/bootstrap.ts (webpack) + the AMD loader.
// The loader's two jobs are covered by Vite (see MIGRATION-GUIDE §4):
//   1. "include every widget the parser may ask for" — webpack did this via the
//      generated widgets-root manifest + cshtml scanner. Here import.meta.glob
//      eagerly imports EVERY module under Typescript/ (plus the dijit/dojox lite
//      widgets shipped in mds-dojo), so any data-dojo-type a Razor view emits —
//      including server-side-DSL-generated ones — resolves. No manifest.
//   2. "resolve an AMD id at runtime" — widgets SELF-REGISTER at module eval
//      (mdsWidget(X, '<module-id>'), emitted by the codemod) exactly like AMD
//      dep-loading made them available; the glob loop below additionally maps
//      every module for sync mdsRequire("id") lookups and re-registers parser
//      types (harmless duplicates).

import './bootstrap-globals'; // app-level globals; dojo/dijit globals self-publish in mds modules
import parser from 'dojo/parser';
import registry from 'dijit/registry';
import ready from 'dojo/ready';
import { registerModules } from 'dojo/registerModules';
import wireHostBindings from './hostBindings';

// Every project module (widgets register with the parser; infra modules simply
// evaluate). Excluded: d.ts (type-only), the AMD-era entry files, this file,
// dojo build profiles — and the _loadingHelpers AGGREGATORS: they are not
// widgets, and the eager glob's `import * as` would force a value-captured
// namespace object per module, which inside the aggregators' import cycles
// reads class bindings before initialization (TDZ crash at load).
const projectModules = import.meta.glob(
  [
    '/Typescript/**/*.{ts,js}',
    '!/Typescript/**/*.d.ts',
    '!/Typescript/**/*.profile.js',
    '!/Typescript/**/_loadingHelpers/**',
    // Same namespace-freeze hazard: pure re-export hubs living inside widget
    // import cycles. Not widgets — nothing loses registration.
    '!/Typescript/WidgetCollection/MainCatalogsEditor/MainCatalogEditorTools.ts',
    '!/Typescript/main.ts',
    '!/Typescript/hostBindings.ts',
    '!/Typescript/bootstrap.ts',
    '!/Typescript/bootstrap-globals.ts',
    '!/Typescript/widgets-root.ts',
    '!/Typescript/widgets-root-autoresolved.ts',
  ],
  { eager: true },
);
// mds-dojo modules registered by dojo id:
//   - dijit/dojox WIDGETS referenced by data-dojo-type in views (parser types), AND
//   - dojo/dijit/dojox CORE modules so the SYNCHRONOUS mdsRequire("id") path resolves
//     them. The cshtml inline scripts (and legacy `require("id")` sites the codemod
//     rewrites to mdsRequire) ask for `dojo/topic`, `dojo/_base/lang`, … — those live
//     under mds-dojo/src/dojo and MUST be in the registry or mdsRequire throws
//     "module not registered". They're already bundled (widgets import them); this
//     only adds the id → module mapping.
const builtinModules = import.meta.glob(
  ['/mds-dojo/src/dojo/**/*.ts', '/mds-dojo/src/dijit/**/*.ts', '/mds-dojo/src/dojox/**/*.ts',
    // hmr-runtime + dom-merge + hmr-listeners + hmr-toast + css-hmr are DEV-ONLY, reached solely via
    // dev-server-injected import.meta.hot code (mds-dojo/vite/mds-hmr.mjs, apply:'serve') or the
    // DEV-gated dynamic import below, and each other. Exclude them from the eager glob so they are
    // NOT pulled into the production bundle (no runtime role there). hmr.ts (the build-safe
    // hmrKeep/hmrBind opt-in helpers) is intentionally NOT excluded — shipped widgets may import it
    // and it degrades to a no-op in prod.
    '!/mds-dojo/src/dojo/_base/hmr-runtime.ts',
    '!/mds-dojo/src/dojo/_base/dom-merge.ts',
    '!/mds-dojo/src/dojo/_base/hmr-listeners.ts',
    '!/mds-dojo/src/dojo/_base/hmr-toast.ts',
    '!/mds-dojo/src/dojo/_base/css-hmr.ts'],
  { eager: true },
);

// Module file path → dojo module-id:
//   /Typescript/moreCore/UI/Edit/Edit.ts   → moreCore/UI/Edit/Edit
//   /mds-dojo/src/dijit/form/TextBox.ts    → dijit/form/TextBox
//   /mds-dojo/src/dojo/topic.ts            → dojo/topic
function pathToId(path: string): string | null {
  let m = path.match(/^\/Typescript\/(.+)\.(?:ts|js)$/);
  if (m) { return m[1]; }
  m = path.match(/^\/mds-dojo\/src\/(dojo|dijit|dojox)\/(.+)\.ts$/);
  if (m) { return m[1] + '/' + m[2]; }
  return null;
}

// Register every globbed module with the runtime registries: by id for synchronous
// mdsRequire("id") lookups (legacy require() sites the codemod rewrote), and each
// default-export ctor with the parser for data-dojo-type resolution. The glob already
// excludes the entry / AMD-era infra ids, so no skip-list is needed here.
registerModules({ ...projectModules, ...builtinModules }, { pathToId });

// DEV-only introspection hook. The widget HMR coordinator (dojo/_base/hmr-runtime,
// injected per-widget-module by the mds-hmr Vite plugin) reads `window.__mds.registry`
// to find the LIVE instances of a hot-patched class, so it can drive their opt-in
// _hmrDispose / _hmrRefresh / _hmrReplace hooks (and the structural escape hatch). A
// behaviour patch still reaches instances via the shared prototype without it; this is
// what makes the opt-in hooks fire. `vite build` sets import.meta.env.DEV = false, so
// terser drops this whole branch — the hook never ships in production.
if (import.meta.env.DEV) {
  (window as any).__mds = { registry, parser };
  // DEV-only CSS live-reload client (paired with the mds-css-reload Vite plugin): swaps a changed
  // stylesheet's <link href> in place, no page reload. Dynamic import so terser drops it (and the
  // glob-excluded module) from the production bundle.
  import('dojo/_base/css-hmr').catch(() => { /* dev-only */ });
}

// No window.mdsRequire global: module code imports `mdsRequire` from 'dojo/require'
// directly, and the Razor layouts' former inline `mdsRequire([...], cb)` scripts (topic
// publishes, the login popup) now live in ./hostBindings, wired on ready below. That
// removes both the global and the deferred-bundle load race it caused.
ready(() => {
  parser.parse();
  wireHostBindings();
});
